import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';
import { SurveyFields } from '../components/SurveyFields';
import SurveyProgress from '../components/SurveyProgress';
import { Toast } from '../components/SurveyToast';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';

export default function SurveyPreviewPage() {
  const { apiFetch } = useSurveyRuntimeApi();
  const { id } = useParams();
  const { showError } = useSurveyUiToast();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubmitToast, setShowSubmitToast] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch(`/admin/surveys/${id}/preview`);
        if (!cancelled) setSurvey(data);
      } catch (e) {
        if (!cancelled) {
          const message = getApiErrorMessage(e, 'Failed to load preview.');
          setErr(message);
          showError(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, id, showError]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (err || !survey) {
    return (
      <div className="page">
        <p className="muted">Preview unavailable right now.</p>
        <Link className="btn secondary" to={`/survey/${id}/edit`}>
          Back to editor
        </Link>
      </div>
    );
  }

  return (
    <div className="page preview-embed">
      {showSubmitToast && (
        <Toast
          message="Preview complete — nothing was submitted."
          variant="success"
          duration={3000}
          onDismiss={() => setShowSubmitToast(false)}
        />
      )}
      <header className="page-header">
        <div className="preview-banner">Preview — responses are not saved</div>
        <h1>{survey.title}</h1>
        <p className="lede">
          This is how respondents will see the survey.{' '}
          <Link to={`/survey/${id}/edit`}>Return to editor</Link>
        </p>
      </header>
      <SurveyProgress survey={survey} answers={answers} />
      {survey.banner_image && (
        <div className="public-survey-banner">
          <img
            src={survey.banner_image}
            alt=""
            style={{
              objectPosition: `${survey.banner_position?.x ?? 50}% ${survey.banner_position?.y ?? 50}%`,
            }}
          />
        </div>
      )}
      <div className="preview-card">
        {survey.logo_image && (
          <img src={survey.logo_image} alt="" className="public-survey-logo" />
        )}
        {survey.description?.trim() && <p className="public-survey-lede">{survey.description}</p>}
        <SurveyFields
          survey={survey}
          answers={answers}
          setAnswers={setAnswers}
          onSubmit={() => setShowSubmitToast(true)}
          submitLabel="Submit (preview)"
        />
      </div>
    </div>
  );
}
